/**
 * Real server mutations the presenter actions call through the Apollo client.
 * Product documents are reused where their variables fit; the override-visit
 * document is demo-owned because the product's declares the warning log id as
 * required (the schema does not).
 */
import { ApolloError, gql, type ApolloClient } from "@apollo/client";
import {
  CalendarBlockEventStatusEnum,
  CancelVisitDocument,
  CommandIndexCombinedDataDocument,
  CreateCalendarBlockDocument,
  CreatePatientDocument,
  CustomUpdateVisitStatusDocument,
  GetSimpleDriveTimeDocument,
  UpdateCalendarBlockMutationDocument,
  UpdateCalendarBlockStatusDocument,
  UpdateShiftStatusDocument,
  UpdateVisitResourceMutationDocument,
  ShiftStatusEnum,
  VisitEventStatusEnum,
  type CancelVisitMutation,
  type CancelVisitMutationVariables,
  type CommandIndexCombinedDataQuery,
  type CommandIndexCombinedDataQueryVariables,
  type CreateCalendarBlockMutation,
  type CreateCalendarBlockMutationVariables,
  type CreatePatientMutation,
  type CreatePatientMutationVariables,
  type GetSimpleDriveTimeQuery,
  type GetSimpleDriveTimeQueryVariables,
  type UpdateCalendarBlockMutationMutation,
  type UpdateCalendarBlockMutationMutationVariables,
  type UpdateCalendarBlockStatusMutation,
  type UpdateCalendarBlockStatusMutationVariables,
  type HomeHealthDemoResetMutation,
  type HomeHealthDemoResetMutationVariables,
  type HomeHealthDemoScheduleOverrideVisitMutation,
  type HomeHealthDemoScheduleOverrideVisitMutationVariables,
  type CustomUpdateVisitStatusMutation,
  type CustomUpdateVisitStatusMutationVariables,
  type UpdateShiftStatusMutation,
  type UpdateShiftStatusMutationVariables,
  type UpdateVisitResourceMutationMutation,
  type UpdateVisitResourceMutationMutationVariables,
} from "~/graphql/__generated__/graphql";

export const COMPASSUS_DEMO_SCHEDULE_OVERRIDE_VISIT = gql`
  mutation HomeHealthDemoScheduleOverrideVisit(
    $patientId: Int!
    $serviceLineId: Int!
    $organizationalUnitId: Int
    $visitTypeId: Int!
    $startTime: String!
    $resources: [NewOverrideVisitResourceInput!]!
    $serviceIds: [Int!]!
    $duration: Int!
    $reschedulingVisitId: Int
    $serviceInstructions: String
    $note: String
  )
  @permission(requires: createOverrideVisits, check: [userInServiceLine])
  {
    scheduleOverrideVisit(
      patientId: $patientId
      serviceLineId: $serviceLineId
      organizationalUnitId: $organizationalUnitId
      visitTypeId: $visitTypeId
      startTime: $startTime
      resources: $resources
      serviceIds: $serviceIds
      duration: $duration
      reschedulingVisitId: $reschedulingVisitId
      serviceInstructions: $serviceInstructions
      note: $note
    ) {
      id
      startTime
      endTime
    }
  }
`;

/**
 * Reset database (`apps/service/services/resetHomeHealthDemo.ts`): the reset and
 * every seed file in one transaction.
 */
export const HOME_HEALTH_DEMO_RESET = gql`
  mutation HomeHealthDemoReset($demoDate: String!, $asOf: String!, $phone: String!)
  @permission(requires: copyDemoData)
  {
    resetHomeHealthDemo(demoDate: $demoDate, asOf: $asOf, phone: $phone) {
      demoDate
      files
      statements
    }
  }
`;

export type DemoClient = ApolloClient<object>;

/** Operation names the horizon and the command center listen to; refetched after every real change. */
export const REFETCH_AFTER_MUTATION = ["HomeHealthDemoHorizon", "CommandIndexCombinedData", "VisitWorklistWarnings"];

/**
 * After Reset database: the horizon first, so this browser reads the new seed
 * stamp and the command center remounts (`components/FreshOnReseed.tsx`) before
 * its own refetch could diff the new shifts against the old and reprice them.
 */
export async function refetchAfterReset(client: DemoClient): Promise<void> {
  await client.refetchQueries({ include: ["HomeHealthDemoHorizon"] });
  await refetchAfterMutation(client);
}

export async function refetchAfterMutation(client: DemoClient): Promise<void> {
  await client.refetchQueries({ include: REFETCH_AFTER_MUTATION });
}

export async function scheduleOverrideVisit(client: DemoClient, variables: HomeHealthDemoScheduleOverrideVisitMutationVariables): Promise<{ id: number; startTime: string; endTime: string }> {
  const { data } = await client.mutate<HomeHealthDemoScheduleOverrideVisitMutation, HomeHealthDemoScheduleOverrideVisitMutationVariables>({ mutation: COMPASSUS_DEMO_SCHEDULE_OVERRIDE_VISIT, variables });
  if (!data) throw new Error("scheduleOverrideVisit returned no data");
  return data.scheduleOverrideVisit;
}

/**
 * The service's own words for a refused request. A refusal is an HTTP 400, which
 * Apollo raises as a network error ("Received status code 400") with the
 * service's GraphQL errors left in the response body.
 */
export function serviceErrorMessage(error: unknown): string | undefined {
  if (!(error instanceof ApolloError)) return undefined;
  const body = (error.networkError as { result?: { errors?: { message?: string }[] } } | null)?.result;
  return body?.errors?.[0]?.message ?? error.graphQLErrors[0]?.message;
}

export async function resetDemoTenant(client: DemoClient, variables: HomeHealthDemoResetMutationVariables): Promise<HomeHealthDemoResetMutation["resetHomeHealthDemo"]> {
  const { data } = await client
    .mutate<HomeHealthDemoResetMutation, HomeHealthDemoResetMutationVariables>({ mutation: HOME_HEALTH_DEMO_RESET, variables })
    .catch((error: unknown) => {
      // The presenter log and the phone field read the refusal, not the status code.
      const message = serviceErrorMessage(error);
      throw message ? new Error(message) : error;
    });
  if (!data) throw new Error("resetHomeHealthDemo returned no data");
  return data.resetHomeHealthDemo;
}

export async function updateVisitStatus(client: DemoClient, visitId: number, status: VisitEventStatusEnum, eventTime?: string): Promise<void> {
  const variables: CustomUpdateVisitStatusMutationVariables = { visitId, newEventStatus: status, autoGenerated: false, eventTime: eventTime ?? null };
  await client.mutate<CustomUpdateVisitStatusMutation, CustomUpdateVisitStatusMutationVariables>({ mutation: CustomUpdateVisitStatusDocument, variables });
}

export async function cancelVisit(client: DemoClient, visitId: number, visitCancellationReasonId: number): Promise<void> {
  const variables: CancelVisitMutationVariables = { visitId, visitCancellationReasonId };
  await client.mutate<CancelVisitMutation, CancelVisitMutationVariables>({ mutation: CancelVisitDocument, variables });
}

/**
 * A patient keyed in by hand on the referrals screen, created for real through
 * the product's own document. The server geocodes the address on create
 * (`geocodeIfUpdated`), which is what the scheduler needs: without coordinates
 * `scheduleOption` cannot place anybody near them.
 */
export async function createPatient(client: DemoClient, variables: CreatePatientMutationVariables): Promise<{ id: number; firstName: string; lastName: string }> {
  const res = await client.mutate<CreatePatientMutation, CreatePatientMutationVariables>({ mutation: CreatePatientDocument, variables });
  const created = res.data?.createPatient;
  if (!created) throw new Error("createPatient returned nothing");
  return { id: created.id, firstName: created.firstName, lastName: created.lastName };
}

export async function setShiftStatus(client: DemoClient, id: number, status: ShiftStatusEnum): Promise<void> {
  const variables: UpdateShiftStatusMutationVariables = { id, status };
  await client.mutate<UpdateShiftStatusMutation, UpdateShiftStatusMutationVariables>({ mutation: UpdateShiftStatusDocument, variables });
}

/** The three drive columns the product keeps on a visit resource, in its own model. */
export interface DriveMinutes {
  startOfShiftDriveTime: number;
  interVisitDriveTime: number;
  endOfShiftDriveTime: number;
}

/** What one write against a visit resource means to change. Anything left out is not sent. */
export interface VisitResourceUpdate {
  id: number;
  providerId?: number;
  startIso?: string;
  endIso?: string;
  drive?: DriveMinutes;
}

/**
 * One write against the command centre's own `UpdateVisitResourceMutation`,
 * carrying only the fields it means to change.
 *
 * A `null` variable is **not** "leave it alone" on this mutation. The resolver
 * hands `startTime` to `convertToDate`, which short-circuits on `undefined`
 * only — `new Date(null)` is the 1970 epoch and passes its `isNaN` check — and
 * it hands the three drive values straight into `NOT NULL` columns. An absent
 * variable is the safe way to say nothing changed, so every field here is
 * omitted unless it is given, which is also what keeps a drive-only write off
 * the service's `scheduleChanged` path and out of its push notification.
 */
export async function moveVisitResource(client: DemoClient, update: VisitResourceUpdate): Promise<void> {
  const variables = {
    id: update.id,
    ...(update.providerId !== undefined ? { providerId: update.providerId } : {}),
    ...(update.startIso !== undefined ? { startTime: update.startIso } : {}),
    ...(update.endIso !== undefined ? { endTime: update.endIso } : {}),
    ...(update.drive ?? {}),
  } as UpdateVisitResourceMutationMutationVariables;
  await client.mutate<UpdateVisitResourceMutationMutation, UpdateVisitResourceMutationMutationVariables>({ mutation: UpdateVisitResourceMutationDocument, variables });
}

/** A block on a clinician's day (3.2). The product geocodes the address on create and recomputes the day's drive segments around it. */
export async function createCalendarBlock(client: DemoClient, variables: CreateCalendarBlockMutationVariables): Promise<number> {
  const { data } = await client.mutate<CreateCalendarBlockMutation, CreateCalendarBlockMutationVariables>({ mutation: CreateCalendarBlockDocument, variables });
  const id = data?.createCalendarBlock?.id;
  if (id === undefined || id === null) throw new Error("createCalendarBlock returned no id");
  return id;
}

/**
 * A block re-timed on My day (3.2), through the command centre's own block-drag
 * document — `UpdateCalendarBlockMutation`, the one `views/command/index.tsx`
 * saves a dragged block with. It is already in the persisted-operations
 * allowlist, so no new document and no service redeploy; it takes the same
 * `createDragEvents` permission the visit move does.
 *
 * Only the two times are sent. Every other variable is omitted rather than sent
 * as `null`, for the same reason the visit move omits them: the resolver writes
 * what it is given, and an absent variable is the only way this mutation says
 * "unchanged". Leaving the address fields out also keeps the service's
 * `isAddressable` branch off the block's address, which an explicit empty
 * address would clear.
 *
 * **The server reprices the day.** `services/calendarBlock.ts` runs
 * `updateDriveTimesForCalendarBlock` inside the same transaction, which
 * recomputes the whole shift and writes every visit resource's drive minutes —
 * so unlike a visit move (M2), a block move needs no client-side reprice and no
 * paid `GetSimpleDriveTime` call. A block with no address coordinates is not a
 * routing stop (`getShiftAndCalendarEvents` filters those out), so moving it
 * changes no leg of the day.
 */
export async function moveCalendarBlock(client: DemoClient, update: { id: number; startIso: string; endIso: string }): Promise<void> {
  // `Exact<>` wants every variable spelled out; the cast is how the visit move
  // omits one too, and omitting is the point — see above.
  const variables = { id: update.id, startTime: update.startIso, endTime: update.endIso } as UpdateCalendarBlockMutationMutationVariables;
  await client.mutate<UpdateCalendarBlockMutationMutation, UpdateCalendarBlockMutationMutationVariables>({ mutation: UpdateCalendarBlockMutationDocument, variables });
}

/** The inverse of a created block: cancel it. */
export async function setCalendarBlockStatus(client: DemoClient, id: number, eventStatus: CalendarBlockEventStatusEnum): Promise<void> {
  const variables: UpdateCalendarBlockStatusMutationVariables = { id, eventStatus };
  await client.mutate<UpdateCalendarBlockStatusMutation, UpdateCalendarBlockStatusMutationVariables>({ mutation: UpdateCalendarBlockStatusDocument, variables });
}

/**
 * The command centre's own day read, used only where the horizon is too thin:
 * address coordinates for the drive-time engine and a block's address. Network-only
 * so a sequence proposal never scores a stale day.
 */
export async function fetchCommandDay(client: DemoClient, variables: CommandIndexCombinedDataQueryVariables): Promise<CommandIndexCombinedDataQuery> {
  const { data } = await client.query<CommandIndexCombinedDataQuery, CommandIndexCombinedDataQueryVariables>({ query: CommandIndexCombinedDataDocument, variables, fetchPolicy: "network-only" });
  return data;
}

/** The product's drive-time engine, asked what an ordering of a day would cost. */
export async function simpleDriveTime(client: DemoClient, variables: GetSimpleDriveTimeQueryVariables): Promise<GetSimpleDriveTimeQuery["simpleDriveTime"]> {
  const { data } = await client.query<GetSimpleDriveTimeQuery, GetSimpleDriveTimeQueryVariables>({ query: GetSimpleDriveTimeDocument, variables, fetchPolicy: "network-only" });
  return data?.simpleDriveTime ?? [];
}

export { CalendarBlockEventStatusEnum, ShiftStatusEnum, VisitEventStatusEnum };
